import { arrayMove } from '@dnd-kit/sortable';

import {
  PageRecipient,
  PageRecipientVisibility,
  RECIPIENT_TYPE,
  ValidRecipientType,
} from 'src/commons/types';
import { sortObjectsByAscending } from 'src/commons/utils/ArrayUtils';

type MovePageRecipientOrderParams = {
  pageRecipients: PageRecipient[];
  pageRecipienToMoveId: string;
  newOrderIndex: number;
  newVisibility: PageRecipientVisibility;
};

type SortedAndGroupedPageRecipientsReturn<K> = {
  sortedFeaturedPageRecipients: K;
  sortedUnfeaturedPageRecipients: K;
  sortedHiddenPageRecipients: K;
  sortedTogetherViewFeaturedPageRecipients: K;
};

export function sortPageRecipientsAscending(
  pageRecipientA: PageRecipient,
  pageRecipientB: PageRecipient
) {
  return (pageRecipientA.order as number) - (pageRecipientB.order as number);
}

export function movePageRecipientOrder(params: MovePageRecipientOrderParams) {
  const { pageRecipients, pageRecipienToMoveId, newOrderIndex, newVisibility } =
    params;

  const sortedAndGroupedPageRecipients =
    sortAndGroupPageRecipients(pageRecipients);
  const {
    sortedFeaturedPageRecipients: featuredPageRecipients,
    sortedUnfeaturedPageRecipients: unfeaturedPageRecipients,
    sortedHiddenPageRecipients: hiddenPageRecipients,
    sortedTogetherViewFeaturedPageRecipients:
      togetherViewFeaturedPageRecipients,
  } = sortedAndGroupedPageRecipients;
  const newPageRecipientsOrder = {
    [PageRecipientVisibility.FEATURED]: featuredPageRecipients,
    [PageRecipientVisibility.UNFEATURED]: unfeaturedPageRecipients,
    [PageRecipientVisibility.HIDDEN]: hiddenPageRecipients,
    [PageRecipientVisibility.TOGETHER_VIEW_FEATURED]:
      togetherViewFeaturedPageRecipients,
  };

  const pageRecipientToMove = pageRecipients.find(
    (pageRecipient) => pageRecipient.id === pageRecipienToMoveId
  ) as PageRecipient;

  const isMovedToDifferentVisibility =
    newVisibility !== pageRecipientToMove.visibility;

  if (isMovedToDifferentVisibility) {
    const pageRecipientGroupToMoveFrom = getPageRecipientGroup(
      pageRecipientToMove.visibility,
      sortedAndGroupedPageRecipients
    );
    const pageRecipientGroupToMoveTo = getPageRecipientGroup(
      newVisibility,
      sortedAndGroupedPageRecipients
    );

    const pageRecipientGroupToMoveFromNewIndexOrder =
      pageRecipientGroupToMoveFrom.filter(
        (pageRecipient) => pageRecipient.id !== pageRecipientToMove.id
      );

    const pageRecipientGroupToMoveToNewIndexOrder: PageRecipient[] = [
      ...pageRecipientGroupToMoveTo.slice(0, newOrderIndex),
      { ...pageRecipientToMove, visibility: newVisibility },
      ...pageRecipientGroupToMoveTo.slice(newOrderIndex),
    ];

    const pageRecipientGroupToMoveFromWithUpdatedOrderProp =
      updatePageRecipientOrderPropBasedOnIndexPosition(
        pageRecipientGroupToMoveFromNewIndexOrder
      );

    const pageRecipientGroupToMoveToWithUpdatedOrderProp =
      updatePageRecipientOrderPropBasedOnIndexPosition(
        pageRecipientGroupToMoveToNewIndexOrder
      );

    newPageRecipientsOrder[pageRecipientToMove.visibility] =
      pageRecipientGroupToMoveFromWithUpdatedOrderProp;
    newPageRecipientsOrder[newVisibility] =
      pageRecipientGroupToMoveToWithUpdatedOrderProp;
  } else {
    const pageRecipientGroup = getPageRecipientGroup(
      pageRecipientToMove.visibility,
      sortedAndGroupedPageRecipients
    );
    const pageRecipientGroupNewIndexOrder = arrayMove(
      pageRecipientGroup,
      pageRecipientGroup.indexOf(pageRecipientToMove),
      newOrderIndex
    );

    const pageRecipientGroupWithUpdatedOrderProp =
      updatePageRecipientOrderPropBasedOnIndexPosition(
        pageRecipientGroupNewIndexOrder
      );

    newPageRecipientsOrder[pageRecipientToMove.visibility] =
      pageRecipientGroupWithUpdatedOrderProp;
  }

  return Object.values(newPageRecipientsOrder).flat();
}

function getPageRecipientGroup(
  visibility: PageRecipientVisibility,
  groupedPageRecipients: SortedAndGroupedPageRecipientsReturn<PageRecipient[]>
) {
  switch (visibility) {
    case PageRecipientVisibility.FEATURED:
      return groupedPageRecipients.sortedFeaturedPageRecipients;
    case PageRecipientVisibility.UNFEATURED:
      return groupedPageRecipients.sortedUnfeaturedPageRecipients;
    case PageRecipientVisibility.HIDDEN:
      return groupedPageRecipients.sortedHiddenPageRecipients;
    case PageRecipientVisibility.TOGETHER_VIEW_FEATURED:
      return groupedPageRecipients.sortedTogetherViewFeaturedPageRecipients;
  }
}

function updatePageRecipientOrderPropBasedOnIndexPosition(
  pageRecipients: PageRecipient[]
) {
  return pageRecipients.map((pageRecipient, index) => ({
    ...pageRecipient,
    order: index,
  }));
}

export function sortAndGroupPageRecipients<
  T extends PageRecipient[] | undefined
>(
  pageRecipients: T,
  visibleTypes?: Set<ValidRecipientType>
): SortedAndGroupedPageRecipientsReturn<T> {
  const featuredPageRecipients = pageRecipients?.filter(
    (pageRecipient) =>
      pageRecipient.visibility === PageRecipientVisibility.FEATURED
  );

  const sortedFeaturedPageRecipients =
    featuredPageRecipients &&
    sortObjectsByAscending(featuredPageRecipients, 'order');

  const togetherViewFeaturedPageRecipients = pageRecipients?.filter(
    (pageRecipient) =>
      pageRecipient.visibility ===
      PageRecipientVisibility.TOGETHER_VIEW_FEATURED
  );

  const sortedTogetherViewFeaturedPageRecipients =
    togetherViewFeaturedPageRecipients &&
    sortObjectsByAscending(togetherViewFeaturedPageRecipients, 'order');

  const unfeaturedPageRecipients = pageRecipients
    ?.filter(
      (pageRecipient) =>
        pageRecipient.visibility === PageRecipientVisibility.UNFEATURED
    )
    .filter(
      (recipient) =>
        !visibleTypes ||
        (recipient.recipientTypes &&
          // Show either standard types that are visible
          (recipient.recipientTypes.some(
            (type) => type !== 'political_campaign' && visibleTypes.has(type)
          ) ||
            // OR show political_campaign when OTHER is visible
            (recipient.recipientTypes.includes('political_campaign') &&
              visibleTypes.has(RECIPIENT_TYPE.OTHER))))
    );

  const sortedUnfeaturedPageRecipients =
    unfeaturedPageRecipients &&
    sortObjectsByAscending(unfeaturedPageRecipients, 'order');

  const hiddenPageRecipients = pageRecipients?.filter(
    (pageRecipient) =>
      pageRecipient.visibility === PageRecipientVisibility.HIDDEN
  );

  const sortedHiddenPageRecipients =
    hiddenPageRecipients &&
    sortObjectsByAscending(hiddenPageRecipients, 'order');

  return {
    sortedFeaturedPageRecipients: sortedFeaturedPageRecipients as T,
    sortedUnfeaturedPageRecipients: sortedUnfeaturedPageRecipients as T,
    sortedHiddenPageRecipients: sortedHiddenPageRecipients as T,
    sortedTogetherViewFeaturedPageRecipients:
      sortedTogetherViewFeaturedPageRecipients as T,
  };
}
